import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { X, Loader2, AlertCircle, Building2, Tag, Briefcase, GitBranch, Clock, Save } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../hooks/useAuth';
import { useAppPermissions } from '../../hooks/useAppPermissions';
import { useLaunchDeal } from '../../hooks/useLaunchDeal';

const TABS = [
  { key: 'identidade',     label: 'Identidade',     Icon: Tag },
  { key: 'empresa',        label: 'Empresa',        Icon: Briefcase },
  { key: 'empreendimento', label: 'Empreendimento', Icon: Building2 },
  { key: 'pipeline',       label: 'Pipeline',       Icon: GitBranch },
  { key: 'historico',      label: 'Histórico',      Icon: Clock },
];

const STAGE_COLORS = {
  novo:               'bg-slate-100 text-slate-700',
  em_qualificacao:    'bg-sky-100 text-sky-700',
  proposta_enviada:   'bg-violet-100 text-violet-700',
  em_negociacao:      'bg-amber-100 text-amber-700',
  contrato_assinado:  'bg-emerald-100 text-emerald-700',
  encerrado_ganho:    'bg-green-100 text-green-700',
  encerrado_perdido:  'bg-red-100 text-red-700',
};

function fmtBRL(value) {
  if (value == null) return '—';
  const n = Number(value);
  if (!isFinite(n)) return '—';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}
function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('pt-BR'); } catch { return d; }
}

export default function LaunchDealDetailModal({ dealId, isOpen, onClose }) {
  const { user, loading: authLoading } = useAuth();
  const { permissions } = useAppPermissions();
  const { deal, loading, error, refetch } = useLaunchDeal(dealId);
  const [activeTab, setActiveTab] = useState('identidade');

  const canWrite = permissions.includes('launches.write');

  const [stages, setStages] = useState([]);
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      const buR = await supabase.from('business_units').select('id').eq('code', 'launches').single();
      if (cancelled || !buR.data?.id) return;
      const stR = await supabase
        .from('deal_stages').select('id, nome').eq('business_unit_id', buR.data.id).order('id');
      if (!cancelled) setStages(Array.isArray(stR.data) ? stR.data : []);
    })();
    return () => { cancelled = true; };
  }, [isOpen]);

  const [stageDraft, setStageDraft] = useState('');
  const [nextStepDraft, setNextStepDraft] = useState('');
  const [nextStepDueDraft, setNextStepDueDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    if (deal) {
      setStageDraft(deal.stage || 'novo');
      setNextStepDraft(deal.next_step || '');
      setNextStepDueDraft(deal.next_step_due_date || '');
    }
  }, [deal]);

  const dirty = useMemo(() => {
    if (!deal) return false;
    return (
      (deal.stage || 'novo') !== stageDraft ||
      (deal.next_step || '') !== nextStepDraft ||
      (deal.next_step_due_date || '') !== nextStepDueDraft
    );
  }, [deal, stageDraft, nextStepDraft, nextStepDueDraft]);

  const handleSavePipeline = useCallback(async () => {
    if (authLoading || !user?.id || !canWrite || !deal) return;
    setSaving(true);
    setSaveError(null);
    try {
      const { error: err } = await supabase
        .from('launch_deals')
        .update({
          stage: stageDraft,
          next_step: nextStepDraft.trim() || null,
          next_step_due_date: nextStepDueDraft || null,
          updated_at: new Date().toISOString(),
          updated_by: user.id,
        })
        .eq('id', deal.id);
      if (err) throw err;
      await refetch();
    } catch (e) {
      setSaveError(e.message || String(e));
    } finally {
      setSaving(false);
    }
  }, [authLoading, user, canWrite, deal, stageDraft, nextStepDraft, nextStepDueDraft, refetch]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-[10004] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-slate-200 flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <Building2 size={18} className="text-violet-600 flex-shrink-0" />
              <h2 className="text-base font-bold text-slate-800 truncate">
                {deal?.title || 'Deal — Empreendimentos'}
              </h2>
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
              {deal?.stage && (
                <span className={`px-1.5 py-0.5 rounded ${STAGE_COLORS[deal.stage] || 'bg-slate-100 text-slate-700'}`}>
                  {deal.stage}
                </span>
              )}
              {deal?.status && <span className="px-1.5 py-0.5 bg-slate-100 rounded">{deal.status}</span>}
              {deal?.empresa && (
                <span className="text-slate-600">
                  {deal.empresa.empresa || deal.empresa.company_name}
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 flex-shrink-0" title="Fechar">
            <X size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-2 border-b border-slate-200 bg-slate-50 flex items-center gap-0.5 overflow-x-auto">
          {TABS.map(({ key, label, Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`px-2.5 py-1.5 text-xs flex items-center gap-1.5 transition-colors border-b-2 -mb-px ${
                activeTab === key
                  ? 'border-violet-500 text-violet-700 font-semibold'
                  : 'border-transparent text-slate-600 hover:bg-slate-100'
              }`}
            >
              <Icon size={12} /> {label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {loading && (
            <div className="flex items-center gap-2 text-xs text-slate-500 py-6 justify-center">
              <Loader2 size={14} className="animate-spin" /> Carregando deal...
            </div>
          )}
          {!loading && error && (
            <div className="p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800 flex items-start gap-1.5">
              <AlertCircle size={12} className="flex-shrink-0 mt-0.5" /> {error.message || String(error)}
            </div>
          )}
          {!loading && !error && !deal && (
            <div className="text-xs text-slate-500 py-6 text-center">Deal não encontrado.</div>
          )}

          {!loading && deal && activeTab === 'identidade' && (
            <div className="space-y-2 text-xs">
              <Field label="Título">{deal.title}</Field>
              <Field label="Descrição">{deal.description || '—'}</Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Stage">{deal.stage}</Field>
                <Field label="Status">{deal.status}</Field>
                <Field label="Valor estimado">{fmtBRL(deal.valor_estimado)}</Field>
                <Field label="Source">{deal.source || '—'}</Field>
                <Field label="Criado em">{fmtDate(deal.created_at)}</Field>
                <Field label="Vencimento próximo passo">{fmtDate(deal.next_step_due_date)}</Field>
              </div>
            </div>
          )}

          {!loading && deal && activeTab === 'empresa' && (
            <div className="space-y-2 text-xs">
              {deal.empresa ? (
                <>
                  <Field label="Empresa">
                    {deal.empresa.empresa || deal.empresa.company_name || '—'}
                  </Field>
                  {deal.empresa.cnpj && <Field label="CNPJ"><span className="font-mono">{deal.empresa.cnpj}</span></Field>}
                  <div className="text-[11px] text-slate-500 italic">
                    Detalhe completo da empresa: abrir via /contatos ou /fila-empresas (EmpresaQuickViewModal cross-links em SET futuro).
                  </div>
                </>
              ) : (
                <div className="text-slate-500 italic">Empresa não vinculada.</div>
              )}
            </div>
          )}

          {!loading && deal && activeTab === 'empreendimento' && (
            <div className="text-xs text-slate-600 space-y-2">
              <div className="p-3 bg-slate-50 border border-slate-200 rounded">
                <div className="font-semibold mb-1">Empreendimento Órulo</div>
                <div>orulo_building_id atual: <span className="font-mono">{deal.orulo_building_id ?? 'NULL'}</span></div>
                <div className="text-[11px] text-slate-500 mt-2 italic">
                  Mirror Órulo (orulo_buildings, orulo_developers) será entregue no SET 4 (ADR-017).
                </div>
              </div>
            </div>
          )}

          {!loading && deal && activeTab === 'pipeline' && (
            <div className="space-y-3 text-xs">
              {!canWrite && (
                <div className="p-2 bg-amber-50 border border-amber-200 rounded text-amber-800 text-[11px]">
                  Você só tem permissão de leitura (`launches.read`). Edição requer `launches.write`.
                </div>
              )}
              {saveError && (
                <div className="p-2 bg-red-50 border border-red-200 rounded text-red-800 flex items-start gap-1.5">
                  <AlertCircle size={12} className="flex-shrink-0 mt-0.5" /> {saveError}
                </div>
              )}
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Stage</label>
                <select
                  value={stageDraft}
                  disabled={!canWrite || saving}
                  onChange={(e) => setStageDraft(e.target.value)}
                  className="w-full px-2 py-1.5 text-xs border border-slate-300 rounded bg-white disabled:bg-slate-100 disabled:cursor-not-allowed"
                >
                  {(stages.length ? stages : [{ nome: stageDraft }]).map((s) => (
                    <option key={s.nome} value={s.nome}>{s.nome}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Próximo passo</label>
                <input
                  type="text"
                  value={nextStepDraft}
                  disabled={!canWrite || saving}
                  onChange={(e) => setNextStepDraft(e.target.value)}
                  placeholder="Descrição do próximo passo"
                  className="w-full px-2 py-1.5 text-xs border border-slate-300 rounded disabled:bg-slate-100 disabled:cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Vencimento</label>
                <input
                  type="date"
                  value={nextStepDueDraft}
                  disabled={!canWrite || saving}
                  onChange={(e) => setNextStepDueDraft(e.target.value)}
                  className="w-full px-2 py-1.5 text-xs border border-slate-300 rounded disabled:bg-slate-100 disabled:cursor-not-allowed"
                />
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleSavePipeline}
                  disabled={!canWrite || !dirty || saving || authLoading || !user?.id}
                  className="px-3 py-1.5 text-xs bg-violet-600 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 hover:bg-violet-700"
                >
                  {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                  {saving ? 'Salvando...' : 'Salvar pipeline'}
                </button>
              </div>
            </div>
          )}

          {!loading && deal && activeTab === 'historico' && (
            <div className="text-xs text-slate-500 italic p-3 bg-slate-50 border border-slate-200 rounded">
              Activity stream em construção (ADR-016 — backbone existe; integração visual no SET futuro).
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
      <div className="text-slate-800">{children}</div>
    </div>
  );
}
